import { FC, useEffect, useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";
import "./dev.scss";

import {
  getRmsExportError,
  patchRmsExportErrorSubmission,
  postRmsExportErrorTestExport,
  postRmsExportErrorReexport,
  RmsExportErrorDetail,
} from "@rubi/api-generated";

import { Breadcrumbs, Crumb, DevPage, ErrorMsg, Section } from "./common";

export const DevRmsExportErrorDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<RmsExportErrorDetail | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [dataText, setDataText] = useState("");
  const [jsonValid, setJsonValid] = useState(true);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadDetail = async () => {
    setErrorMsg(null);
    const response = await getRmsExportError({
      path: { error_id: id! },
      throwOnError: false,
    });
    if (response.status === 200) {
      const data = response.data!;
      setDetail(data);
      setDataText(JSON.stringify(data.submission_data, null, 2));
    } else {
      setErrorMsg("" + response.status + " " + response.error?.console_msg);
    }
  };

  useEffect(() => {
    loadDetail();
  }, [id]);

  const handleDataChange = (value: string) => {
    setDataText(value);
    try {
      JSON.parse(value);
      setJsonValid(true);
    } catch {
      setJsonValid(false);
    }
  };

  const handleSave = async () => {
    if (!jsonValid) return;
    setSaving(true);
    setErrorMsg(null);
    const parsed = JSON.parse(dataText);
    const response = await patchRmsExportErrorSubmission({
      path: { error_id: id! },
      body: { data: parsed },
      throwOnError: false,
    });
    if (response.status === 204) {
      setTestResult("Saved successfully.");
    } else {
      setErrorMsg("" + response.status + " " + response.error?.console_msg);
    }
    setSaving(false);
  };

  const handleTestExport = async () => {
    setTesting(true);
    setTestResult(null);
    setErrorMsg(null);
    const response = await postRmsExportErrorTestExport({
      path: { error_id: id! },
      throwOnError: false,
    });
    if (response.status === 200) {
      const result = response.data!;
      if (result.success) {
        setTestResult(
          `Export successful. ${result.xml_data.length} record(s) generated.\n\n` +
            result.xml_data.join("\n---\n"),
        );
      } else {
        setTestResult(`Export failed: ${result.error}`);
      }
    } else {
      setErrorMsg("" + response.status + " " + response.error?.console_msg);
    }
    setTesting(false);
  };

  const handleReexport = async () => {
    if (
      !window.confirm(
        "This will clear the export time and delete this error record. The submission will be picked up for re-export. Continue?",
      )
    ) {
      return;
    }
    setErrorMsg(null);
    const response = await postRmsExportErrorReexport({
      path: { error_id: id! },
      throwOnError: false,
    });
    if (response.status === 204) {
      navigate("/dev_admin/rms_export_error");
    } else {
      setErrorMsg("" + response.status + " " + response.error?.console_msg);
    }
  };

  if (!detail && !errorMsg) {
    return (
      <DevPage title="Loading...">
        <Section>
          <p>Loading...</p>
        </Section>
      </DevPage>
    );
  }

  return (
    <DevPage
      title="RMS Export Error Detail"
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <Link to="/">Home</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin">Dev Admin</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin/rms_export_error">RMS Export Errors</Link>
          </Crumb>
          <Crumb>Detail</Crumb>
        </Breadcrumbs>
      }
    >
      <ErrorMsg msg={errorMsg} />
      {detail && (
        <>
          <Section subtitle="Error Info">
            <table className="dev-table dev-table--details">
              <tbody>
                <tr>
                  <td>Org</td>
                  <td>{detail.org_title}</td>
                </tr>
                <tr>
                  <td>Tracking Number</td>
                  <td>
                    {detail.tracking_number || (
                      <span className="muted">N/A</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Survey</td>
                  <td>{detail.survey_name}</td>
                </tr>
                <tr>
                  <td>Time</td>
                  <td className="muted">
                    {new Date(detail.time_created).toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              style={{
                background: "var(--action-hover)",
                border: "1px solid #e55",
                borderRadius: 4,
                padding: 12,
                marginTop: 12,
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
              }}
            >
              {detail.error_message}
            </div>
            <p style={{ marginTop: 8 }}>
              <Link
                to={`/rubireportv2/${detail.rubi_org_id}/form-management/survey/${detail.parent_survey_id}`}
              >
                Open Survey Editor
              </Link>
            </p>
          </Section>

          <Section subtitle="Submission Data (JSON)">
            <textarea
              value={dataText}
              onChange={(e) => handleDataChange(e.target.value)}
              style={{
                width: "100%",
                minHeight: 400,
                fontFamily: "monospace",
                fontSize: 13,
                border: jsonValid ? "1px solid #ccc" : "2px solid red",
                borderRadius: 4,
                padding: 8,
              }}
            />
            {!jsonValid && (
              <p style={{ color: "red", margin: "4px 0" }}>Invalid JSON</p>
            )}
          </Section>

          <Section subtitle="Actions">
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={handleTestExport} disabled={testing}>
                {testing ? "Testing..." : "Test Export"}
              </button>
              <button onClick={handleSave} disabled={saving || !jsonValid}>
                {saving ? "Saving..." : "Save"}
              </button>
              <button onClick={handleReexport}>Re-export</button>
            </div>
          </Section>

          {testResult && (
            <Section subtitle="Test Result">
              <pre
                style={{
                  background: "var(--action-hover)",
                  padding: 12,
                  borderRadius: 4,
                  whiteSpace: "pre-wrap",
                  maxHeight: 500,
                  overflow: "auto",
                }}
              >
                {testResult}
              </pre>
            </Section>
          )}
        </>
      )}
    </DevPage>
  );
};
